import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge, buttonClassName, Card, ListRow, Wordmark } from '../../ui';
import {
  listCoachNotes,
  listEntries,
  listSessions,
  listThreadMessages,
  listWeeks,
} from '../../shared/queries';
import type { SessionRow, WeekRow } from '../../shared/queries';
import { formatDate, formatMoment, formatTime, weekdayNames } from '../../shared/formatDate';
import { supabase } from '../../shared/supabase/client';
import { useInstance } from '../instance/InstanceProvider';
import { useSession } from '../session/SessionProvider';
import { text } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { ClientPage } from './ClientPage';
import { ChevronRight } from './icons';
import { useSessionProgress } from './progress';
import { unreadMessages, unreadNotes, usePresence } from './presence';
import {
  currentWeek,
  nextSession,
  sessionDay,
  sessionToday,
  today,
  weekAfter,
  weekdayOf,
} from './schedule';
import type { ISODay } from './schedule';

/**
 * The first screen a client sees, and the one they will see most.
 *
 * **It answers one question before anything else**: is there a session for me
 * today, and what is it. Everything else on the screen is below that answer, and
 * the answer is above the fold on a 400 px phone with nothing to scroll and
 * nothing to press first.
 *
 * The states are the work here, not the happy path (`client-daily-loop` D5).
 * There are four, and "there is nothing today" is both the most ordinary one and
 * the one most likely to be rendered as an error or an empty void:
 *
 *   1. **a session today** — its title, how many exercises, that it is from
 *      their coach, and one control that starts it;
 *   2. **nothing today** — said plainly, with when the next one is where a next
 *      one is known, and with nothing that suggests the client is at fault;
 *   3. **nothing published at all** — their coach is still preparing it, and
 *      nothing is asked of the client;
 *   4. **today's session already done** — with the time, and a control that
 *      offers to look at it rather than to start it again.
 *
 * **There is no tenant filter in this file.** `listWeeks` selects every week the
 * caller may see, and the policy `week_select_client_published` is what makes
 * that "my own weeks, and only the published ones". A draft week is not hidden
 * here — it is not in the response at all.
 *
 * **What is still below the answer, and why it has not moved.** The published
 * week's sessions are listed under it, as they were before this change. It is
 * what tells a client the plan is more than one day, and five end-to-end tests
 * read the publish gate, the delete gate and week duplication through it;
 * removing it would have meant rewriting those assertions in the same change
 * that rebuilt the screen they point at, which is how a suite quietly gets
 * weaker.
 *
 * `client-week-and-plan` added **one link** under that list, to the week screen,
 * and changed nothing else here. The week screen is where a client goes to look
 * back and ahead; this list stays because Today should still be able to answer
 * "what else is this week" without a second screen.
 */
export function TodayPage() {
  return (
    <ClientGuard>
      <Today />
    </ClientGuard>
  );
}

function Today() {
  const instance = useInstance();
  const { profile, signOut } = useSession();
  const now = today();

  const weeks = useQuery({ queryKey: ['weeks'], queryFn: () => listWeeks(supabase) });

  const published = weeks.data ?? [];
  const current = weeks.data ? currentWeek(published, now) : null;
  const following = current === null ? null : weekAfter(published, current);

  const sessions = useQuery({
    queryKey: ['sessions', current?.id],
    queryFn: () => listSessions(supabase, current?.id ?? ''),
    enabled: current !== null,
  });

  // The week after this one, asked for only so that "when is the next session"
  // still has an answer on a Sunday. Two requests, never one per week.
  const followingSessions = useQuery({
    queryKey: ['sessions', following?.id],
    queryFn: () => listSessions(supabase, following?.id ?? ''),
    enabled: following !== null,
  });

  const thisWeeksSessions = sessions.data ?? [];
  const todaySession = current === null ? null : sessionToday(current, thisWeeksSessions, now);

  const entries = useQuery({
    queryKey: ['entries', todaySession?.id],
    queryFn: () => listEntries(supabase, todaySession?.id ?? ''),
    enabled: todaySession !== null,
  });

  const progress = useSessionProgress(todaySession?.id ?? '');

  const upcoming =
    current === null
      ? null
      : nextSession(
          [
            { week: current, sessions: thisWeeksSessions },
            ...(following === null
              ? []
              : [{ week: following, sessions: followingSessions.data ?? [] }]),
          ],
          now,
        );

  return (
    <ClientPage>
      <header className="flex items-center justify-between gap-3 py-2">
        <Wordmark
          name={instance.displayName}
          text={instance.wordmarkText}
          logoUrl={instance.logoUrl}
          size="sm"
        />
        <button
          type="button"
          className="text-ink-muted min-h-11 text-xs underline-offset-2 hover:underline"
          onClick={() => void signOut()}
        >
          {text('client.today.signOut')}
        </button>
      </header>

      <p className="text-ink-muted mt-2 text-xs tracking-wide uppercase">
        {text('client.today.dateLine', {
          weekday: weekdayNames()[weekdayOf(now) - 1] ?? '',
          date: formatDate(now),
        })}
      </p>

      <h1 className="text-ink font-display mt-1 text-3xl leading-tight">
        {profile?.display_name === undefined
          ? text('client.today.title')
          : text('client.today.greeting', { name: profile.display_name })}
      </h1>

      {weeks.isPending ? (
        <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p>
      ) : null}

      {/* State 3: nothing published at all. Nothing is asked of the client. */}
      {!weeks.isPending && current === null ? (
        <Card className="mt-4">
          <h2 className="text-ink font-display text-xl">{text('client.today.nothingTitle')}</h2>
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {text('client.today.nothingBody', { coach: instance.coachDisplayName })}
          </p>
        </Card>
      ) : null}

      {current === null || sessions.isPending ? null : todaySession === null ? (
        <NothingToday upcoming={upcoming} />
      ) : (
        <TodaysSession
          slug={instance.slug}
          session={todaySession}
          coach={instance.coachDisplayName}
          exerciseCount={entries.data?.length ?? null}
          completedAt={progress.completedAt}
        />
      )}

      {current === null ? null : (
        <ThisWeek slug={instance.slug} week={current} sessions={thisWeeksSessions} now={now} />
      )}

      <WithYourCoach slug={instance.slug} coach={instance.coachDisplayName} />

      {/*
        The fifth destination, and **still not a tab bar**.

        `client-me` builds the last of the five `mvp-discovery` → `client-app`
        describes, so for the first time all five exist and the choice between
        three tabs and five is actually makeable. It is still not this change's
        to make — `client-me`'s spec says nothing about navigation either — so
        the entry point goes exactly where the other three went: on Today, as an
        ordinary link. If the owner picks a bar, these rows move into it and
        nothing about any of the five screens changes.
      */}
      <p className="mt-8">
        <Link
          to={`/c/${instance.slug}/me`}
          className="text-primary inline-flex min-h-11 items-center gap-1 text-sm font-medium underline-offset-2 hover:underline"
        >
          {text('client.me.link')}
          <ChevronRight size={18} />
        </Link>
      </p>
    </ClientPage>
  );
}

/**
 * State 1 and state 4: there is a session today.
 *
 * One card, on the mint ground the design uses for the thing that matters, with
 * exactly one control in it. The control's label changes — start it, carry on
 * with it, look at it — but there is never a second control competing with it.
 *
 * The exercise count waits for its own request rather than blocking the card:
 * the title and the control are the answer, and they are on screen before the
 * count arrives.
 */
function TodaysSession({
  slug,
  session,
  coach,
  exerciseCount,
  completedAt,
}: {
  slug: string;
  session: SessionRow;
  coach: string;
  exerciseCount: number | null;
  completedAt: string | null;
}) {
  const done = completedAt !== null;

  return (
    <Card tone="soft" className="mt-4">
      <p className="text-primary text-xs font-semibold tracking-widest uppercase">
        {done ? text('client.today.doneEyebrow') : text('client.today.todayEyebrow')}
      </p>

      <h2 className="text-ink font-display mt-1 text-2xl leading-tight">{session.title}</h2>

      <p className="text-ink-soft mt-1 text-sm">
        {exerciseCount === null
          ? text('client.today.fromCoach', { coach })
          : `${count(exerciseCount)} · ${text('client.today.fromCoach', { coach })}`}
      </p>

      {done ? (
        <p className="text-ink-soft mt-3 text-sm">
          {text('client.today.doneAt', { time: formatTime(completedAt) })}
        </p>
      ) : null}

      {session.coach_note === null || done ? null : (
        <p className="text-ink mt-3 text-sm leading-relaxed">{session.coach_note}</p>
      )}

      {/*
        A link, not a button, wearing the button's clothes: it navigates, so it
        can be opened in a new tab and is announced as what it is. A `<button>`
        inside an `<a>` would be invalid and would sit in the tab order twice.
      */}
      <Link
        to={`/c/${slug}/sessions/${session.id}`}
        className={buttonClassName({
          variant: done ? 'secondary' : 'accent',
          size: 'lg',
          className: 'mt-4',
        })}
      >
        {done ? text('client.today.view') : text('client.today.start')}
      </Link>
    </Card>
  );
}

/**
 * State 2: nothing today.
 *
 * The most ordinary state there is, and the one a screen is most likely to
 * render as an error. So it is an ordinary card with an ordinary sentence, it
 * carries no alarm colour and no illustration of emptiness, and it says when the
 * next session is where one is known — which is the thing the client actually
 * wants to know next.
 */
function NothingToday({ upcoming }: { upcoming: { session: SessionRow; day: ISODay } | null }) {
  return (
    <Card className="mt-4">
      <h2 className="text-ink font-display text-xl">{text('client.today.noSessionTitle')}</h2>
      <p className="text-ink-soft mt-2 text-sm">{text('client.today.noSessionBody')}</p>

      {/*
        A sentence, not a link. The next session is reachable from the week
        below, and a control here would invite a client to open a session that
        is not today's from the screen whose whole job is to say what today's is.
      */}
      <p className="text-ink-soft mt-1 text-sm">
        {upcoming === null
          ? text('client.today.nextNone')
          : text('client.today.nextIs', {
              title: upcoming.session.title,
              weekday: weekdayNames()[weekdayOf(upcoming.day) - 1] ?? '',
            })}
      </p>
    </Card>
  );
}

/** The published week's sessions, under the answer. See the note in the header. */
function ThisWeek({
  slug,
  week,
  sessions,
  now,
}: {
  slug: string;
  week: WeekRow;
  sessions: readonly SessionRow[];
  now: ISODay;
}) {
  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-ink font-display text-lg">
          {text('client.today.week', { number: week.week_number })}
        </h2>
        <Badge tone="training">{text('client.today.published')}</Badge>
        {week.starts_on === null ? null : (
          <span className="text-ink-muted text-sm">
            {text('client.today.from', { date: formatDate(week.starts_on) })}
          </span>
        )}
      </div>

      {sessions.length === 0 ? (
        <Card className="mt-3">
          <p className="text-ink-soft text-sm">{text('client.today.noSessions')}</p>
        </Card>
      ) : (
        <ul className="mt-2">
          {sessions.map((session) => (
            <li key={session.id}>
              <Link
                to={`/c/${slug}/sessions/${session.id}`}
                className="block rounded-[--radius-control]"
              >
                <ListRow
                  title={session.title}
                  meta={weekdayNames()[sessionWeekday(week, session, now)] ?? ''}
                  trailing={<ChevronRight size={20} className="text-ink-muted" />}
                />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {/*
        The way into the week, and **still not a tab bar**.

        `client-week-and-plan` builds the second of the five destinations
        `mvp-discovery` describes, and the hi-fi has three. That disagreement is
        an information-architecture decision belonging to the owner, and this
        change's spec says nothing about navigation either — so the entry point
        goes where `client-daily-loop` and `client-coach-presence` put theirs: on
        Today, as an ordinary link, next to the week it opens. If the owner picks
        a bar, it moves into the bar and nothing about the week screen changes.
      */}
      <Link
        to={`/c/${slug}/week`}
        className="text-primary mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-medium underline-offset-2 hover:underline"
      >
        {text('client.week.seeWeek')}
        <ChevronRight size={18} />
      </Link>

      <p className="text-ink-muted mt-3 text-xs">
        {text('client.today.updated', { moment: formatMoment(week.updated_at) })}
      </p>
    </section>
  );
}

/**
 * The three ways to reach the person coaching them, and the unread state that
 * says something is waiting.
 *
 * **This is not a tab bar, and its absence is still a live question.**
 * `client-daily-loop` shipped no bottom navigation because the hi-fi has three
 * tabs, `mvp-discovery` → `client-app` describes five, and four of the five
 * destinations did not exist. This slice builds three of them, so they have to
 * be reachable — but which of the two information architectures is right is the
 * owner's decision and `client-coach-presence`'s spec says nothing about
 * navigation at all. Answering it here, by building whichever bar the
 * implementer preferred, would settle a question nobody asked this change to
 * settle and would have to be undone.
 *
 * So the entry points sit on Today, which is where a client already is, as three
 * ordinary rows. If the owner chooses a tab bar, these rows move into it and
 * nothing else about the three screens changes.
 *
 * **Unread is a word on an amber ground**, never a bare dot: amber means a
 * person needs attention (`design-system`), and a state carried only by a colour
 * is a state some readers do not have. What it counts is on the device — see
 * `./presence.ts` — so it agrees with the thread and the notes list exactly,
 * and with nothing else, because there is nothing else yet.
 */
function WithYourCoach({ slug, coach }: { slug: string; coach: string }) {
  const notes = useQuery({ queryKey: ['coach-notes'], queryFn: () => listCoachNotes(supabase) });
  const thread = useQuery({
    queryKey: ['thread-messages'],
    queryFn: () => listThreadMessages(supabase),
  });
  const presence = usePresence();

  const unreadNoteCount = unreadNotes(notes.data ?? [], presence).length;
  const unreadMessageCount = unreadMessages(thread.data ?? [], presence.threadReadAt).length;

  const rows = [
    {
      to: `/c/${slug}/notes`,
      title: text('client.presence.notes'),
      meta: text('client.presence.notesMeta', { coach }),
      unread: unreadNoteCount > 0,
    },
    {
      to: `/c/${slug}/videos`,
      title: text('client.presence.videos'),
      meta: text('client.presence.videosMeta'),
      unread: false,
    },
    {
      to: `/c/${slug}/messages`,
      title: text('client.presence.messages'),
      meta: text('client.presence.messagesMeta'),
      unread: unreadMessageCount > 0,
    },
  ];

  return (
    <section className="mt-8">
      <h2 className="text-ink font-display text-lg">
        {text('client.presence.section', { coach })}
      </h2>

      <ul className="mt-2">
        {rows.map((row) => (
          <li key={row.to}>
            <Link to={row.to} className="block rounded-[--radius-control]">
              <ListRow
                title={row.title}
                meta={row.meta}
                trailing={
                  row.unread ? (
                    <Badge tone="attention">{text('client.presence.unread')}</Badge>
                  ) : (
                    <ChevronRight size={20} className="text-ink-muted" />
                  )
                }
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Index into `weekdayNames()`, which is Monday-first and zero-based. */
function sessionWeekday(week: WeekRow, session: SessionRow, now: ISODay): number {
  return weekdayOf(sessionDay(week, session, now)) - 1;
}

/** "1 ejercicio" and "6 ejercicios" are two sentences, not one with a number in it. */
function count(exercises: number): string {
  return exercises === 1
    ? text('client.today.exercisesOne')
    : text('client.today.exercisesMany', { count: exercises });
}
